import React, { useState, useRef, useEffect } from 'react';
import L from 'leaflet';
import GpsFixedIcon from '@mui/icons-material/MyLocationOutlined';
import { CircularProgress } from '@mui/material';
import { nominatimService } from '../../../utils/locationSearch.js';
import type { FormattedSearchResult } from '../../../utils/locationSearch.js';
import { getDetailPanelHeight } from '../../../utils/detailPanelUtils.js';
import './MapControls.css';

interface MapControlsProps {
  mapRef: React.MutableRefObject<L.Map | null>;
  onUserLocationChange?: (location: L.LatLng | null) => void;
  onAddRoutePointViaSearch?: (position: { lat: number; lon: number }, pointType?: 'via' | 'end') => void; // For search-based points
  routePointsCount?: number; // Number of existing route points
  onFitMapToRoute?: (fitFunction: (points: Array<{ lat: number; lon: number }>) => void) => void; // Callback to expose fit function
  detailPanelOpen?: boolean; // Whether the detail panel is currently open
}

const MapControlsComponent: React.FC<MapControlsProps> = ({ mapRef, onUserLocationChange, onAddRoutePointViaSearch, routePointsCount = 0, onFitMapToRoute, detailPanelOpen = false }) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<FormattedSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedResultIndex, setSelectedResultIndex] = useState(-1);
  const [hoveredActionIndex, setHoveredActionIndex] = useState(-1);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isGpsLoading, setIsGpsLoading] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<number | null>(null);

  // Detect touch device
  useEffect(() => {
    const checkTouch = () => {
      // More strict touch detection - only consider it a touch device if it's primarily touch-based
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
      const noHover = window.matchMedia('(hover: none)').matches;
      
      const isTouchPrimary = hasTouch && isCoarsePointer && noHover;
      
      setIsTouchDevice(isTouchPrimary);
    };
    checkTouch();
    window.addEventListener('resize', checkTouch);
    return () => window.removeEventListener('resize', checkTouch);
  }, []);

  // Debounced search with 300ms delay
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.trim().length >= 2) {
      searchTimeoutRef.current = window.setTimeout(async () => {
        const currentQuery = searchQuery; // Capture current query
        setIsSearching(true);
        
        try {
          const results = await nominatimService.searchFormatted(currentQuery, 5);
          
          // Only update if the query hasn't changed in the meantime
          if (currentQuery === searchQuery) {
            setSearchResults(results);
          }
        } catch (error) {
          console.error('Search error:', error);
          
          // Only update if the query hasn't changed in the meantime
          if (currentQuery === searchQuery) {
            setSearchResults([]);
          }
        } finally {
          // Only update if the query hasn't changed in the meantime
          if (currentQuery === searchQuery) {
            setIsSearching(false);
          }
        }
      }, 300);
    } else {
      setSearchResults([]);
      setIsSearching(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]); // Only depend on searchQuery

  // Focus the input when search opens
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  const handleGpsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!mapRef.current) return;
    
    if (!navigator.geolocation) {
      alert('Geolokalisierung wird von diesem Browser nicht unterstützt.');
      return;
    }

    setIsGpsLoading(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (!mapRef.current) return;
        const { latitude, longitude } = position.coords;
        const latlng = L.latLng(latitude, longitude);
        mapRef.current.setView([latitude, longitude], 15);
        
        // Benachrichtige Parent-Komponente über neue GPS-Position
        if (onUserLocationChange) {
          onUserLocationChange(latlng);
        }
        
        setIsGpsLoading(false);
      },
      (error) => {
        console.error('GPS Error:', error);
        setIsGpsLoading(false);
        let message = 'Standort konnte nicht ermittelt werden.';
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            message = 'Berechtigung für Standortermittlung verweigert.';
            break;
          case error.POSITION_UNAVAILABLE:
            message = 'Standortinformationen sind nicht verfügbar.';
            break;
          case error.TIMEOUT:
            message = 'Timeout bei der Standortermittlung.';
            break;
        }
        
        alert(message);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  };

  // Zoom configuration
  const ZOOM_SKIP_LEVELS = 1; // How many levels to skip on single click
  const ZOOM_HOLD_INTERVAL = 150; // ms between zoom steps when holding
  const ZOOM_HOLD_DELAY = 500; // ms delay before starting continuous zoom

  // Zoom state
  const [isZoomingIn, setIsZoomingIn] = useState(false);
  const [isZoomingOut, setIsZoomingOut] = useState(false);
  const zoomTimeoutRef = useRef<number | null>(null);
  const zoomIntervalRef = useRef<number | null>(null);
  
  // Handle click events in safety zone to prevent them from reaching the map
  const handleSafetyZoneClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent click from reaching the map
    // Only preventDefault on mouse events, not touch events which may be passive
    if (e.type === 'click') {
      e.preventDefault();
    }
  };

  const handleZoomIn = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!mapRef.current) return;
    
    // Single click: skip multiple zoom levels
    const currentZoom = mapRef.current.getZoom();
    const targetZoom = Math.min(currentZoom + ZOOM_SKIP_LEVELS, mapRef.current.getMaxZoom());
    mapRef.current.setZoom(targetZoom);
  };

  const handleZoomOut = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!mapRef.current) return;
    
    // Single click: skip multiple zoom levels
    const currentZoom = mapRef.current.getZoom();
    const targetZoom = Math.max(currentZoom - ZOOM_SKIP_LEVELS, mapRef.current.getMinZoom());
    mapRef.current.setZoom(targetZoom);
  };

  // Start continuous zoom on mouse/touch down
  const handleZoomInStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    // Only preventDefault on mouse events to avoid passive event listener warnings
    if (e.type === 'mousedown') {
      e.preventDefault();
    }
    
    if (isZoomingIn) return;
    setIsZoomingIn(true);
    
    // Start continuous zooming after delay
    zoomTimeoutRef.current = window.setTimeout(() => {
      if (!mapRef.current) return;
      
      zoomIntervalRef.current = window.setInterval(() => {
        if (!mapRef.current) return;
        
        const currentZoom = mapRef.current.getZoom();
        const maxZoom = mapRef.current.getMaxZoom();
        
        if (currentZoom < maxZoom) {
          mapRef.current.zoomIn();
        } else {
          handleZoomStop();
        }
      }, ZOOM_HOLD_INTERVAL);
    }, ZOOM_HOLD_DELAY);
  };

  const handleZoomOutStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    // Only preventDefault on mouse events to avoid passive event listener warnings
    if (e.type === 'mousedown') {
      e.preventDefault();
    }
    
    if (isZoomingOut) return;
    setIsZoomingOut(true);
    
    // Start continuous zooming after delay
    zoomTimeoutRef.current = window.setTimeout(() => {
      if (!mapRef.current) return;
      
      zoomIntervalRef.current = window.setInterval(() => {
        if (!mapRef.current) return;
        
        const currentZoom = mapRef.current.getZoom();
        const minZoom = mapRef.current.getMinZoom();
        
        if (currentZoom > minZoom) {
          mapRef.current.zoomOut();
        } else {
          handleZoomStop();
        }
      }, ZOOM_HOLD_INTERVAL);
    }, ZOOM_HOLD_DELAY);
  };

  // Stop continuous zoom
  const handleZoomStop = () => {
    if (zoomTimeoutRef.current) {
      clearTimeout(zoomTimeoutRef.current);
      zoomTimeoutRef.current = null;
    }
    
    if (zoomIntervalRef.current) {
      clearInterval(zoomIntervalRef.current);
      zoomIntervalRef.current = null;
    }
    
    setIsZoomingIn(false);
    setIsZoomingOut(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      handleZoomStop();
    };
  }, []);

  // Handle mouse leave and global mouse up events
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      handleZoomStop();
    };

    const handleGlobalTouchEnd = () => {
      handleZoomStop();
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    document.addEventListener('touchend', handleGlobalTouchEnd);
    document.addEventListener('touchcancel', handleGlobalTouchEnd);

    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('touchend', handleGlobalTouchEnd);
      document.removeEventListener('touchcancel', handleGlobalTouchEnd);
    };
  }, []);

  const handleSearchClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newIsOpen = !isSearchOpen;
    setIsSearchOpen(newIsOpen);
    
    if (!newIsOpen) {
      // Clear search when closing
      setSearchQuery('');
      setSearchResults([]);
      setSelectedResultIndex(-1);
    }
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setSelectedResultIndex(-1);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedResultIndex >= 0 && searchResults[selectedResultIndex]) {
      selectSearchResult(searchResults[selectedResultIndex]);
    } else if (searchResults.length > 0) {
      selectSearchResult(searchResults[0]);
    }
  };

  const selectSearchResult = (result: FormattedSearchResult) => {
    if (!mapRef.current) return;
    
    // Move map to selected location
    mapRef.current.setView([result.lat, result.lon], 15);
    
    // Close search
    setIsSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedResultIndex(-1);
  };

  const addSearchResultAsRoutePoint = (result: FormattedSearchResult, e: React.MouseEvent, pointType: 'via' | 'end' = 'via') => {
    e.stopPropagation(); // Prevent triggering the main click handler
    
    if (onAddRoutePointViaSearch) {
      onAddRoutePointViaSearch({ lat: result.lat, lon: result.lon }, pointType);
    } else {
      console.error('onAddRoutePointViaSearch is not defined!');
    }
    
    // Close search after adding route point
    setIsSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedResultIndex(-1);
  };

  // Function to fit map view to show all routing points
  const fitMapToRoute = (routePoints: Array<{ lat: number; lon: number }>) => {
    try {
      if (routePoints.length === 1) {
        // For single point, center the map accounting for DetailPanel if open
        const point = routePoints[0];
        
        if (detailPanelOpen) {
          // When DetailPanel is open, treat single point as a small area with padding
          // Create a small bounds around the point and use fitBounds with DetailPanel padding
          const latOffset = 0.001; // Small offset to create bounds
          const lngOffset = 0.001;
          
          const bounds = L.latLngBounds([
            [point.lat - latOffset, point.lon - lngOffset],
            [point.lat + latOffset, point.lon + lngOffset]
          ]);
          
          // Use the same padding logic as multi-point
          const basePadding = 160; // Increased from 120 for more comfortable spacing around route points
          const statsBarHeight = 60; // Fixed StatsBar height
          const detailPanelHeight = getDetailPanelHeight();
          const totalBottomPadding = statsBarHeight + detailPanelHeight + 80; // Added 80px extra for better centering
          
          const paddingOptions: L.FitBoundsOptions = {
            padding: [basePadding, basePadding] as L.PointTuple,
            paddingTopLeft: [0, 0] as L.PointTuple,
            paddingBottomRight: [0, totalBottomPadding] as L.PointTuple,
            maxZoom: 2
          };
          
          mapRef.current!.fitBounds(bounds, paddingOptions);
        } else {
          // DetailPanel closed - but still need to account for StatsBar
          // Use fitBounds with StatsBar padding instead of setView to ensure proper spacing
          const latOffset = 0.001; // Small offset to create bounds
          const lngOffset = 0.001;
          
          const bounds = L.latLngBounds([
            [point.lat - latOffset, point.lon - lngOffset],
            [point.lat + latOffset, point.lon + lngOffset]
          ]);
          
          const basePadding = 160; // Increased padding for comfortable spacing
          const statsBarHeight = 60; // Fixed StatsBar height
          
          const paddingOptions: L.FitBoundsOptions = {
            padding: [basePadding, basePadding] as L.PointTuple,
            paddingTopLeft: [0, 0] as L.PointTuple,
            paddingBottomRight: [0, statsBarHeight] as L.PointTuple,
            maxZoom: 14
          };
          
          mapRef.current!.fitBounds(bounds, paddingOptions);
        }
      } else {
        // For multiple points, fit bounds to include all points
        const bounds = L.latLngBounds(routePoints.map(point => [point.lat, point.lon]));
        
        // Calculate comfortable padding for all sides
        const defaultPadding = 200; // Increased from 160 for even more generous spacing around route
        const statsBarHeight = 60; // Fixed StatsBar height
        
        // Individual padding control for each edge
        const paddingTop = 100;    // Top edge padding
        const paddingRight = 50;  // Right edge padding  
        const paddingLeft = 80;   // Left edge padding
        let paddingBottom = statsBarHeight + 40; // Bottom edge padding + StatsBar height + extra space
        
        // Add extra bottom padding when DetailPanel is open
        if (detailPanelOpen) {
          // DetailPanel height + additional centering offset for better visual balance
          paddingBottom += getDetailPanelHeight() + 80; // Added 80px extra for better centering
        }
        
        const paddingOptions: L.FitBoundsOptions = {
          padding: [defaultPadding, defaultPadding] as L.PointTuple, // [horizontal, vertical] generous default padding
          paddingTopLeft: [paddingLeft, paddingTop] as L.PointTuple, // [left, top] individual control
          paddingBottomRight: [paddingRight, paddingBottom] as L.PointTuple, // [right, bottom] individual control
          maxZoom: 14 // Prevent zooming in too much for short routes while still showing good detail
        };
        
        mapRef.current!.fitBounds(bounds, paddingOptions);
      }
    } catch (error) {
      console.error('Error fitting map to route:', error);
    }
  };

  // Expose the fitMapToRoute function to parent component
  React.useEffect(() => {
if (onFitMapToRoute) {
      onFitMapToRoute(fitMapToRoute);
    }
  }, [onFitMapToRoute, detailPanelOpen, fitMapToRoute]);

  // SVG Icon components
  const PlusIcon = () => (
    <svg className="search-result-add-icon" viewBox="0 0 24 24" fill="none">
      <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );

  // New professional ViaIcon with gradients and shadows (adapted from your design)
  const ViaIcon = () => (
    <svg className="search-result-add-icon" viewBox="0 0 28 20" fill="none">
      {/* Gradient definitions */}
      <defs>
        {/* Dark gradient for highlighted markers */}
        <linearGradient id="darkGradientVia" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style={{stopColor:"#9A9A9A", stopOpacity:1}} />
          <stop offset="100%" style={{stopColor:"#5A5A5A", stopOpacity:1}} />
        </linearGradient>
        
        {/* Light gradient for background markers */}
        <linearGradient id="lightGradientVia" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style={{stopColor:"#E8E8E8", stopOpacity:1}} />
          <stop offset="100%" style={{stopColor:"#C0C0C0", stopOpacity:1}} />
        </linearGradient>
        
        {/* Shadow filter */}
        <filter id="shadowVia" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0.3" dy="0.6" stdDeviation="0.5" floodColor="rgba(0,0,0,0.3)"/>
        </filter>
      </defs>
      
      {/* Via icon - middle highlighted, centered */}
      <g transform="translate(-6, -4) scale(0.35)">
        {/* Connecting lines */}
        <line x1="40" y1="40" x2="60" y2="60" stroke="#C0C0C0" strokeWidth="2" opacity="0.8"/>
        <line x1="60" y1="60" x2="80" y2="40" stroke="#C0C0C0" strokeWidth="2" opacity="0.8"/>
        
        {/* Left marker (smaller, light grey) */}
        <path d="M 40 20 C 36 20, 32 24, 32 28 C 32 32, 40 40, 40 40 C 40 40, 48 32, 48 28 C 48 24, 44 20, 40 20 Z" 
              fill="url(#lightGradientVia)" stroke="#A8A8A8" strokeWidth="0.5" filter="url(#shadowVia)"/>
        
        {/* Right marker (smaller, light grey) */}
        <path d="M 80 20 C 76 20, 72 24, 72 28 C 72 32, 80 40, 80 40 C 80 40, 88 32, 88 28 C 88 24, 84 20, 80 20 Z" 
              fill="url(#lightGradientVia)" stroke="#A8A8A8" strokeWidth="0.5" filter="url(#shadowVia)"/>
        
        {/* Center marker (larger, darker grey) */}
        <path d="M 60 25 C 53 25, 47 31, 47 38 C 47 45, 60 60, 60 60 C 60 60, 73 45, 73 38 C 73 31, 67 25, 60 25 Z" 
              fill="url(#darkGradientVia)" stroke="#505050" strokeWidth="0.5" filter="url(#shadowVia)"/>
      </g>
    </svg>
  );

  // New professional EndIcon with gradients and shadows (adapted from your design)  
  const EndIcon = () => (
    <svg className="search-result-add-icon" viewBox="0 0 28 20" fill="none">
      {/* Gradient definitions */}
      <defs>
        {/* Dark gradient for highlighted markers */}
        <linearGradient id="darkGradientEnd" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style={{stopColor:"#9A9A9A", stopOpacity:1}} />
          <stop offset="100%" style={{stopColor:"#5A5A5A", stopOpacity:1}} />
        </linearGradient>
        
        {/* Light gradient for background markers */}
        <linearGradient id="lightGradientEnd" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style={{stopColor:"#E8E8E8", stopOpacity:1}} />
          <stop offset="100%" style={{stopColor:"#C0C0C0", stopOpacity:1}} />
        </linearGradient>
        
        {/* Shadow filter */}
        <filter id="shadowEnd" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0.3" dy="0.6" stdDeviation="0.5" floodColor="rgba(0,0,0,0.3)"/>
        </filter>
      </defs>
      
      {/* End icon - right highlighted, centered */}
      <g transform="translate(-6, -4) scale(0.35)">
        {/* Connecting lines */}
        <line x1="40" y1="40" x2="50" y2="40" stroke="#C0C0C0" strokeWidth="2" opacity="0.8"/>
        <line x1="50" y1="40" x2="70" y2="60" stroke="#C0C0C0" strokeWidth="2" opacity="0.8"/>
        
        {/* Left marker (smaller, light grey) */}
        <path d="M 40 20 C 36 20, 32 24, 32 28 C 32 32, 40 40, 40 40 C 40 40, 48 32, 48 28 C 48 24, 44 20, 40 20 Z" 
              fill="url(#lightGradientEnd)" stroke="#A8A8A8" strokeWidth="0.5" filter="url(#shadowEnd)"/>
        
        {/* Center marker (smaller, light grey) */}
        <path d="M 50 20 C 46 20, 42 24, 42 28 C 42 32, 50 40, 50 40 C 50 40, 58 32, 58 28 C 58 24, 54 20, 50 20 Z" 
              fill="url(#lightGradientEnd)" stroke="#A8A8A8" strokeWidth="0.5" filter="url(#shadowEnd)"/>
        
        {/* Right marker (larger, darker grey) */}
        <path d="M 70 25 C 63 25, 57 31, 57 38 C 57 45, 70 60, 70 60 C 70 60, 83 45, 83 38 C 83 31, 77 25, 70 25 Z" 
              fill="url(#darkGradientEnd)" stroke="#505050" strokeWidth="0.5" filter="url(#shadowEnd)"/>
      </g>
    </svg>
  );

  // Smart button logic based on route points count
  const getAddButtonConfig = (routePointsCount: number) => {
    if (routePointsCount === 0) {
      // No points: Show Plus button -> adds as start point  
      return {
        showSingleButton: true,
        buttonType: 'via' as const, // Start point is technically a via point in our system
        buttonTitle: 'Add as starting point',
        icon: <PlusIcon />
      };
    } else if (routePointsCount === 1) {
      // One point: Show Plus button -> adds as end point
      return {
        showSingleButton: true,
        buttonType: 'end' as const,
        buttonTitle: 'Add as Target point', 
        icon: <PlusIcon />
      };
    } else {
      // Multiple points: Show Plus button by default, expand on hover to via/end buttons
      return {
        showSingleButton: true, // Changed: start with plus button
        buttonType: 'via' as const, // Default action when plus is clicked
        buttonTitle: 'Add new point (Hover for more options)',
        icon: <PlusIcon />
      };
    }
  };

  const handleSearchInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsSearchOpen(false);
      setSearchQuery('');
      setSearchResults([]);
      setSelectedResultIndex(-1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedResultIndex(prev => 
        prev < searchResults.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedResultIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedResultIndex >= 0 && searchResults[selectedResultIndex]) {
        selectSearchResult(searchResults[selectedResultIndex]);
      } else if (searchResults.length > 0) {
        selectSearchResult(searchResults[0]);
      }
    }
  };

  return (
    <>
      {/* Safety zone wrapper around controls */}
      <div 
        className="map-controls-safety-zone"
        onClick={handleSafetyZoneClick}
        onMouseDown={handleSafetyZoneClick}
        style={{
          position: 'absolute',
          top: '0px', // Start from screen edge
          left: '0px', // Start from screen edge
          width: '100px', // Extended width to cover more area
          height: '100px', // Extended height to cover more area
          zIndex: 2000 // Much higher z-index to ensure it's on top
        }}
      >
        <div className={`map-controls-container ${isTouchDevice ? 'touch-device' : ''}`}>
          <div className="leaflet-bar leaflet-control map-controls-panel">
        {/* Top Left: Zoom In Button */}
        <button
          className={`map-control-button zoom-in-button ${isZoomingIn ? 'zooming-active' : ''}`}
          title="Zoom In (Hold for Continuous Zoom In)"
          onClick={handleZoomIn}
          onMouseDown={handleZoomInStart}
          onMouseUp={handleZoomStop}
          onMouseLeave={handleZoomStop}
          onTouchStart={handleZoomInStart}
          onTouchEnd={handleZoomStop}
          onTouchCancel={handleZoomStop}
        >
          <svg 
            className="map-control-icon"
            viewBox="0 0 24 24"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>

        {/* Top Right: Search Button with expanding input */}
        <div className="search-button-container">
          <button
            className={`map-control-button search-button ${isSearchOpen ? 'active' : ''}`}
            title="Suchen"
            onClick={handleSearchClick}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
          >
            <svg 
              className="map-control-icon"
              viewBox="0 0 24 24"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </button>
          
          {/* Search Input Field - expands from the button */}
          <div className={`search-input-container ${isSearchOpen ? 'expanded' : ''}`}>
            <form onSubmit={handleSearchSubmit} style={{ height: '100%' }}>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search Place or City..."
                value={searchQuery}
                onChange={handleSearchInputChange}
                onKeyDown={handleSearchInputKeyDown}
                className={`search-input ${isSearchOpen ? 'visible' : ''}`}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
              />
            </form>
          </div>

          {/* Search Results Dropdown */}
          {isSearchOpen && (searchResults.length > 0 || isSearching) && (
            <div
              className="search-results-dropdown"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {isSearching && (
                <div className="search-loading">
                  <div className="search-loading-spinner" />
                  Suche läuft...
                </div>
              )}
              
              {searchResults.map((result, index) => (
                <div
                  key={result.id}
                  className={`search-result-item ${result.subLine ? 'two-line' : 'single-line'} ${
                    !isTouchDevice && index === selectedResultIndex && hoveredActionIndex !== index ? 'selected' : ''
                  }`}
                  onMouseEnter={() => {
                    if (!isTouchDevice && hoveredActionIndex !== index) {
                      setSelectedResultIndex(index);
                    }
                  }}
                  onMouseLeave={() => {
                    if (!isTouchDevice) {
                      setSelectedResultIndex(-1);
                    }
                  }}
                  onTouchStart={() => {
                    // On touch devices, clear any hover states
                    if (isTouchDevice) {
                      setSelectedResultIndex(-1);
                      setHoveredActionIndex(-1);
                    }
                  }}
                >
                  {/* Location Icon */}
                  <span 
                    className={`search-result-icon ${result.subLine ? 'two-line' : ''}`}
                    onClick={() => selectSearchResult(result)}
                  >
                    {result.icon}
                  </span>
                  
                  {/* Location Name - Two Lines */}
                  <div
                    className={`search-result-text ${result.subLine ? 'two-line' : ''}`}
                    title={result.displayName} // Full display name as tooltip
                    onClick={() => selectSearchResult(result)}
                  >
                    {/* Primary line (name/street) */}
                    <span className="search-result-main">
                      {result.mainLine}
                    </span>
                    
                    {/* Secondary line (address/context) */}
                    {result.subLine && (
                      <span className="search-result-sub">
                        {result.subLine}
                      </span>
                    )}
                  </div>
                  
                  {/* Right side container for flag and add button */}
                  <div 
                    className={`search-result-actions ${result.subLine ? 'two-line' : ''}`}
                    onClick={(e) => e.stopPropagation()} // Prevent parent click
                    onMouseEnter={() => {
                      if (!isTouchDevice) {
                        setHoveredActionIndex(index);
                        setSelectedResultIndex(-1); // Clear selection when hovering actions
                      }
                    }}
                    onMouseLeave={() => {
                      if (!isTouchDevice) {
                        setHoveredActionIndex(-1);
                      }
                    }}
                    onTouchStart={(e) => {
                      if (isTouchDevice) {
                        e.stopPropagation();
                        setHoveredActionIndex(index);
                        setSelectedResultIndex(-1);
                      }
                    }}
                    onTouchEnd={() => {
                      if (isTouchDevice) {
                        setHoveredActionIndex(-1);
                      }
                    }}
                  >
                    {/* Country Flag */}
                    <span className="search-result-flag" title="Country">
                      {result.countryFlag}
                    </span>
                    
                    {/* Vertical divider */}
                    <div className="search-result-divider" />
                    
                    {/* Add to route buttons - smart behavior based on route points count */}
                    <div 
                      className={`search-result-add-container ${routePointsCount >= 2 ? 'hover-expandable' : 'single-plus-mode'}`}
                      onClick={(e) => e.stopPropagation()} // Prevent parent click
                    >
                      {(() => {
                        const config = getAddButtonConfig(routePointsCount);
                        
                        if (routePointsCount >= 2) {
                          // For 2+ points: Show plus button by default, expand to via/end on hover
                          return (
                            <>
                              {/* Default plus button - visible by default */}
                              <button
                                className="search-result-add-button plus-button default-visible"
                                title={config.buttonTitle}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  addSearchResultAsRoutePoint(result, e, config.buttonType!);
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onTouchStart={(e) => {
                                  if (isTouchDevice) {
                                    e.stopPropagation();
                                  }
                                }}
                                onTouchEnd={(e) => {
                                  if (isTouchDevice) {
                                    e.stopPropagation();
                                  }
                                }}
                              >
                                {config.icon}
                              </button>
                              
                              {/* Via point button - shows on hover */}
                              <button
                                className="search-result-add-button via-button hover-visible"
                                title="Add as Via Point"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  addSearchResultAsRoutePoint(result, e, 'via');
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onTouchStart={(e) => {
                                  if (isTouchDevice) {
                                    e.stopPropagation();
                                  }
                                }}
                                onTouchEnd={(e) => {
                                  if (isTouchDevice) {
                                    e.stopPropagation();
                                  }
                                }}
                              >
                                <ViaIcon />
                              </button>
                              
                              {/* End point button - shows on hover */}
                              <button
                                className="search-result-add-button end-button hover-visible"
                                title="Add as End Point"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  addSearchResultAsRoutePoint(result, e, 'end');
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onTouchStart={(e) => {
                                  if (isTouchDevice) {
                                    e.stopPropagation();
                                  }
                                }}
                                onTouchEnd={(e) => {
                                  if (isTouchDevice) {
                                    e.stopPropagation();
                                  }
                                }}
                              >
                                <EndIcon />
                              </button>
                            </>
                          );
                        } else {
                          // For 0-1 points: Show single plus button only
                          return (
                            <button
                              className="search-result-add-button plus-button"
                              title={config.buttonTitle}
                              onClick={(e) => {
                                e.stopPropagation();
                                addSearchResultAsRoutePoint(result, e, config.buttonType!);
                              }}
                              onMouseDown={(e) => e.stopPropagation()}
                              onTouchStart={(e) => {
                                if (isTouchDevice) {
                                  e.stopPropagation();
                                }
                              }}
                              onTouchEnd={(e) => {
                                if (isTouchDevice) {
                                  e.stopPropagation();
                                }
                              }}
                            >
                              {config.icon}
                            </button>
                          );
                        }
                      })()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bottom Left: Zoom Out Button */}
        <button
          className={`map-control-button zoom-out-button ${isZoomingOut ? 'zooming-active' : ''}`}
          title="Zoom Out (Hold for Continuous Zoom Out)"
          onClick={handleZoomOut}
          onMouseDown={handleZoomOutStart}
          onMouseUp={handleZoomStop}
          onMouseLeave={handleZoomStop}
          onTouchStart={handleZoomOutStart}
          onTouchEnd={handleZoomStop}
          onTouchCancel={handleZoomStop}
        >
          <svg 
            className="map-control-icon"
            viewBox="0 0 24 24"
          >
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>

        {/* Bottom Right: GPS Button */}
        <button
          className="map-control-button gps-button"
          title="Aktueller Standort"
          onClick={handleGpsClick}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
          disabled={isGpsLoading}
        >
          {isGpsLoading ? (
            <CircularProgress 
              size={18} 
              sx={{ 
                color: '#4caf50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }} 
            />
          ) : (
            <GpsFixedIcon className="map-control-icon" />
          )}
        </button>
      </div>
        </div>
      </div>
    </>
  );
};

// Memoized component to prevent unnecessary re-renders
export const MapControls = React.memo(MapControlsComponent, (prevProps, nextProps) => {
  // Only re-render if mapRef, onUserLocationChange, routePointsCount, or detailPanelOpen actually changed
  return prevProps.mapRef === nextProps.mapRef && 
         prevProps.onUserLocationChange === nextProps.onUserLocationChange &&
         prevProps.routePointsCount === nextProps.routePointsCount &&
         prevProps.detailPanelOpen === nextProps.detailPanelOpen;
});
